import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { showError } from "../lib/errorHandler";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine,
} from "recharts";
import { TrendingDown, Loader2, RefreshCw, Target, Factory, Clock, Mail, ClipboardCheck, CheckCircle2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHIFTS = [["", "Todos"], ["morning", "Manhã"], ["afternoon", "Tarde"], ["night", "Noite"]];
const PERIODS = [[7, "7 dias"], [14, "14 dias"], [30, "30 dias"], [90, "90 dias"], [365, "1 ano"]];
const CAPA_STATUS = {
  open: { label: "Aberta", cls: "bg-red-100 text-red-700", next: "in_progress", nextLabel: "Iniciar" },
  in_progress: { label: "Em Curso", cls: "bg-blue-100 text-blue-700", next: "verification", nextLabel: "Para Verificação" },
  verification: { label: "Verificação", cls: "bg-amber-100 text-amber-700", next: "closed", nextLabel: "Fechar" },
  closed: { label: "Fechada", cls: "bg-green-100 text-green-700", next: null },
};
const PRIORITIES = [["low", "Baixa"], ["medium", "Média"], ["high", "Alta"], ["critical", "Crítica"]];

function defaultDueDate() {
  const d = new Date(); d.setDate(d.getDate() + 14);
  return d.toISOString().slice(0, 10);
}

function CapaDialog({ defect, periodDays, onClose, onCreated }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ responsible_id: "", due_date: defaultDueDate(), priority: "high", description: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!defect) return;
    axios.get(`${API}/quality/pareto/responsibles`).then((r) => setUsers(r.data)).catch(() => {});
    setForm({ responsible_id: "", due_date: defaultDueDate(), priority: "high", description: "" });
  }, [defect]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.responsible_id) { toast.error("Escolha o responsável"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/quality/pareto/corrective-actions`, {
        defect: defect.defect, quantity: defect.quantity, pct: defect.pct, top_machine: defect.top_machine, top_shift: defect.top_shift,
        period_days: periodDays, responsible_id: form.responsible_id, due_date: form.due_date, priority: form.priority, description: form.description || null,
      });
      toast.success(`Acção correctiva ${r.data.capa_number} criada`);
      onCreated();
      onClose();
    } catch (err) { showError(err, "Erro ao criar acção correctiva"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!defect} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="capa-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo">Abrir Acção Correctiva</DialogTitle>
          <DialogDescription>Defeito: <b>{defect?.defect}</b> — {defect?.quantity} peças ({defect?.pct}%){defect?.top_machine ? ` · Máquina ${defect.top_machine}` : ""}{defect?.top_shift ? ` · Turno ${defect.top_shift}` : ""}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Responsável *</Label>
            <select value={form.responsible_id} onChange={(e) => setForm({ ...form, responsible_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" required data-testid="capa-responsible">
              <option value="">Selecione...</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.user_code})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Prazo *</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} required data-testid="capa-due-date" />
            </div>
            <div className="space-y-2">
              <Label>Prioridade</Label>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="capa-priority">
                {PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Descrição (opcional — gerada automaticamente se vazia)</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Contexto adicional, hipóteses de causa..." data-testid="capa-description" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="capa-submit-btn">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Criar Acção</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CapaList({ capas, onAdvance }) {
  if (capas.length === 0) return null;
  return (
    <div className="bg-white rounded-lg border shadow-sm p-4" data-testid="pareto-capa-list">
      <h3 className="font-semibold text-sm flex items-center gap-2 mb-3"><ClipboardCheck className="h-4 w-4" /> Acções Correctivas Pareto — acompanhamento até ao fecho</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr><th className="text-left p-2">Nº</th><th className="text-left p-2">Defeito</th><th className="text-left p-2">Responsável</th><th className="text-left p-2">Prazo</th><th className="text-left p-2">Prioridade</th><th className="text-left p-2">Estado</th><th className="text-right p-2">Acção</th></tr>
          </thead>
          <tbody>
            {capas.map((c) => {
              const st = CAPA_STATUS[c.status] || CAPA_STATUS.open;
              const overdue = c.status !== "closed" && c.due_date && c.due_date.slice(0, 10) < new Date().toISOString().slice(0, 10);
              return (
                <tr key={c.id} className="border-t" data-testid={`capa-row-${c.capa_number}`}>
                  <td className="p-2 font-mono text-xs">{c.capa_number}</td>
                  <td className="p-2 font-medium">{c.source_id}</td>
                  <td className="p-2">{c.responsible_name || "-"}</td>
                  <td className={`p-2 ${overdue ? "text-red-600 font-semibold" : ""}`}>{(c.due_date || "").slice(0, 10)}{overdue ? " (atraso)" : ""}</td>
                  <td className="p-2 capitalize">{c.priority}</td>
                  <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs font-medium ${st.cls}`} data-testid={`capa-status-${c.capa_number}`}>{st.label}</span></td>
                  <td className="p-2 text-right">
                    {st.next ? (
                      <Button size="sm" variant="outline" onClick={() => onAdvance(c, st.next)} data-testid={`capa-advance-${c.capa_number}`}>
                        {st.next === "closed" ? <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> : <ArrowRight className="h-3.5 w-3.5 mr-1" />}{st.nextLabel}
                      </Button>
                    ) : <span className="text-xs text-slate-400">Fechada {c.closed_at ? c.closed_at.slice(0, 10) : ""}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, color = "#0b1f3a", icon: Icon, testId }) {
  return (
    <div className="bg-white rounded-lg border p-4 shadow-sm" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wide">
        {Icon && <Icon className="h-3.5 w-3.5" />} {label}
      </div>
      <div className="text-3xl font-black mt-1" style={{ color }}>{value ?? "-"}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function BreakdownTable({ title, icon: Icon, rows, total, testId }) {
  return (
    <div className="bg-white rounded-lg border shadow-sm p-4" data-testid={testId}>
      <h3 className="font-semibold text-sm flex items-center gap-2 mb-3"><Icon className="h-4 w-4" /> {title}</h3>
      {rows.length === 0 ? <p className="text-xs text-slate-400">Sem rejeições no período</p> : (
        <div className="space-y-2">
          {rows.map((r) => {
            const pct = total ? (r.quantity / total) * 100 : 0;
            return (
              <div key={r.label}>
                <div className="flex justify-between text-sm"><span className="font-medium">{r.label}</span><span>{r.quantity} <span className="text-slate-400">({pct.toFixed(0)}%)</span></span></div>
                <div className="h-2 bg-slate-100 rounded"><div className="h-2 rounded bg-red-500" style={{ width: `${pct}%` }} /></div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ParetoWaste() {
  const [days, setDays] = useState(7);
  const [machineId, setMachineId] = useState("");
  const [shift, setShift] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [capas, setCapas] = useState([]);
  const [capaDefect, setCapaDefect] = useState(null);
  const [sending, setSending] = useState(false);

  const loadCapas = useCallback(() => {
    axios.get(`${API}/quality/pareto/corrective-actions`).then((r) => setCapas(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadCapas(); }, [loadCapas]);

  const openCapaFor = (defect) => capas.find((c) => c.source_id === defect && c.status !== "closed");

  const advanceCapa = async (c, next) => {
    try {
      await axios.put(`${API}/quality/pareto/corrective-actions/${c.id}/status?status=${next}`);
      toast.success(next === "closed" ? `${c.capa_number} fechada` : "Estado actualizado");
      loadCapas();
    } catch (e) { showError(e, "Erro ao actualizar estado"); }
  };

  const sendEmailNow = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/quality/pareto/send-email-now`);
      toast.success(`Email Pareto enviado para ${r.data.recipients.join(", ")}`);
    } catch (e) { showError(e, "Erro ao enviar email"); }
    finally { setSending(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days });
      if (machineId) params.set("machine_id", machineId);
      if (shift) params.set("shift", shift);
      const r = await axios.get(`${API}/quality/pareto?${params}`);
      setData(r.data);
    } catch { toast.error("Erro a carregar Pareto"); }
    finally { setLoading(false); }
  }, [days, machineId, shift]);

  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="pareto-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-chivo text-3xl md:text-4xl font-bold tracking-tight text-foreground">Pareto de Desperdício</h1>
          <p className="text-muted-foreground mt-1">Causas de rejeição por máquina e turno — ataque os 3 principais defeitos da semana</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={sendEmailNow} disabled={sending} data-testid="pareto-send-email-btn">
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />} Enviar Email Semanal
          </Button>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="pareto-refresh-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />} Actualizar
          </Button>
        </div>
      </div>

      <CapaDialog defect={capaDefect} periodDays={days} onClose={() => setCapaDefect(null)} onCreated={loadCapas} />

      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex gap-1" data-testid="pareto-period-filter">
          {PERIODS.map(([d, l]) => (
            <Button key={d} size="sm" variant={days === d ? "default" : "outline"} onClick={() => setDays(d)} data-testid={`pareto-period-${d}`}>{l}</Button>
          ))}
        </div>
        <select value={machineId} onChange={(e) => setMachineId(e.target.value)} className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="pareto-machine-filter">
          <option value="">Todas as máquinas</option>
          {(data?.machines || []).map((m) => <option key={m.id} value={m.id}>{m.code} {m.name ? `- ${m.name}` : ""}</option>)}
        </select>
        <select value={shift} onChange={(e) => setShift(e.target.value)} className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="pareto-shift-filter">
          {SHIFTS.map(([v, l]) => <option key={v} value={v}>{v ? `Turno ${l}` : "Todos os turnos"}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Peças Produzidas" value={data?.total_produced?.toLocaleString("pt-PT")} icon={Factory} testId="pareto-kpi-produced" />
        <Kpi label="Rejeitadas" value={data?.total_rejected?.toLocaleString("pt-PT")} color="#dc2626" icon={TrendingDown} testId="pareto-kpi-rejected" />
        <Kpi label="Taxa de Desperdício" value={data ? `${data.scrap_rate_pct}%` : "-"} color={data?.scrap_rate_pct > 5 ? "#dc2626" : "#16a34a"} icon={Target} testId="pareto-kpi-rate" />
        <Kpi label="Causas Vitais (80%)" value={data?.vital_few_count} sub={`de ${items.length} causas`} color="#f59e0b" icon={Clock} testId="pareto-kpi-vital" />
      </div>

      <div className="grid md:grid-cols-3 gap-4" data-testid="pareto-top3">
        {(data?.top3 || []).map((t, i) => (
          <div key={t.defect} className="rounded-lg border-l-4 bg-white p-4 shadow-sm" style={{ borderLeftColor: ["#dc2626", "#f59e0b", "#0b1f3a"][i] }} data-testid={`pareto-top-${i + 1}`}>
            <div className="text-xs uppercase text-slate-500 tracking-wide">#{i + 1} Defeito a atacar</div>
            <div className="text-lg font-bold mt-1">{t.defect}</div>
            <div className="text-sm text-slate-600">{t.quantity} peças · {t.pct}% do total</div>
            <div className="flex gap-2 mt-2 flex-wrap">
              {t.top_machine && <Badge variant="outline">Máquina: {t.top_machine}</Badge>}
              {t.top_shift && <Badge variant="outline">Turno: {t.top_shift}</Badge>}
            </div>
            <div className="mt-3">
              {openCapaFor(t.defect) ? (
                <div className="flex items-center gap-2 text-xs" data-testid={`pareto-top-${i + 1}-capa`}>
                  <span className={`px-2 py-0.5 rounded font-medium ${(CAPA_STATUS[openCapaFor(t.defect).status] || CAPA_STATUS.open).cls}`}>{openCapaFor(t.defect).capa_number} · {(CAPA_STATUS[openCapaFor(t.defect).status] || CAPA_STATUS.open).label}</span>
                  <span className="text-slate-500">{openCapaFor(t.defect).responsible_name}</span>
                </div>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setCapaDefect(t)} data-testid={`pareto-top-${i + 1}-open-capa`}>
                  <ClipboardCheck className="h-3.5 w-3.5 mr-1" /> Abrir Acção Correctiva
                </Button>
              )}
            </div>
          </div>
        ))}
        {data && data.top3.length === 0 && <p className="text-sm text-slate-500 md:col-span-3" data-testid="pareto-empty">Sem rejeições registadas no período seleccionado.</p>}
      </div>

      <div className="bg-white rounded-lg border shadow-sm p-4" data-testid="pareto-chart">
        <h3 className="font-semibold text-sm mb-3">Diagrama de Pareto — Quantidade rejeitada e % acumulada</h3>
        <ResponsiveContainer width="100%" height={360}>
          <ComposedChart data={items} margin={{ top: 10, right: 20, bottom: 60, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="defect" angle={-30} textAnchor="end" interval={0} height={70} tick={{ fontSize: 11 }} />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" domain={[0, 100]} unit="%" />
            <Tooltip />
            <Legend verticalAlign="top" />
            <ReferenceLine yAxisId="right" y={80} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: "80%", position: "right", fontSize: 11 }} />
            <Bar yAxisId="left" dataKey="quantity" name="Rejeitadas" radius={[4, 4, 0, 0]}>
              {items.map((it, i) => <Cell key={it.defect} fill={i < 3 ? "#dc2626" : "#94a3b8"} />)}
            </Bar>
            <Line yAxisId="right" type="monotone" dataKey="cum_pct" name="% Acumulada" stroke="#0b1f3a" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <CapaList capas={capas} onAdvance={advanceCapa} />

      <div className="grid md:grid-cols-2 gap-4">
        <BreakdownTable title="Rejeições por Máquina" icon={Factory} total={data?.total_rejected} testId="pareto-by-machine"
          rows={(data?.by_machine || []).map((m) => ({ label: m.machine, quantity: m.quantity }))} />
        <BreakdownTable title="Rejeições por Turno" icon={Clock} total={data?.total_rejected} testId="pareto-by-shift"
          rows={(data?.by_shift || []).map((s) => ({ label: s.label, quantity: s.quantity }))} />
      </div>

      {items.length > 0 && (
        <div className="bg-white rounded-lg border shadow-sm overflow-x-auto" data-testid="pareto-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr><th className="text-left p-3">#</th><th className="text-left p-3">Defeito</th><th className="text-right p-3">Qtd</th><th className="text-right p-3">%</th><th className="text-right p-3">% Acum.</th><th className="text-left p-3">Pior máquina</th><th className="text-left p-3">Pior turno</th></tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={it.defect} className={`border-t ${it.cum_pct <= 80 ? "bg-red-50/40" : ""}`}>
                  <td className="p-3 font-mono">{i + 1}</td>
                  <td className="p-3 font-medium">{it.defect}</td>
                  <td className="p-3 text-right">{it.quantity}</td>
                  <td className="p-3 text-right">{it.pct}%</td>
                  <td className="p-3 text-right font-semibold">{it.cum_pct}%</td>
                  <td className="p-3">{it.top_machine || "-"}</td>
                  <td className="p-3">{it.top_shift || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
